import { compact, groupBy, sumBy } from 'lodash';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/Button/Button';
import { Card } from '../../components/Card/Card';
import { Container } from '../../components/Container/Container';
import { FilterBar } from '../../components/FilterBar/FilterBar';
import { Icon } from '../../components/Icon/Icon';
import { SortableList } from '../../components/SortableList/SortableList';
import { t } from '../../locales/translate';
import { eventPath } from '../../routes';
import { graphql } from '../../generated';
import { NetworkStatus, useQuery } from '@apollo/client';
import { formatPercentage, formatRangeOfDays } from '../../util/formatters';
import { useQueryAsState } from '../../model/history';
import { PriceCell } from '../ticketing/Orders/OrderDetails/OrderItemsRow/Price';
import { Stack } from '../../components/Stack/Stack';
import { ViewportObserver } from '../../components/ViewportObserver/ViewportObserver';
import { TimeFrame, lastMonth } from '../../util/datetime';
import { runEventTurnoverExport } from '../../model/exports';

const QUERY = graphql(`
  query EventTurnover($terms: [String!], $from: ISO8601DateTime, $to: ISO8601DateTime, $cursor: String) {
    turnovers(terms: $terms, from: $from, to: $to, after: $cursor) {
      edges {
        node {
          productVariant {
            id
            sku
            description
            physical
            ... on EventProductVariant {
              pricingCategory {
                id
                name
              }
            }
            product {
              id
              name
              artist
              slug
              ... on Event {
                happeningFrom
                happeningUntil
                venue {
                  id
                  name
                  address {
                    city
                  }
                }
              }
            }
          }
          sales {
            count
            brutPrice
            brutTotal
            vatPercentage
            vatTotal
          }
          refunds {
            count
            brutPrice
            brutTotal
            vatPercentage
            vatTotal
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

function parseTimestamp(value: string | undefined): Date | null {
  if (!value) return null;
  const timestamp = parseInt(value, 10);
  if (!(timestamp > 0)) return null;
  return new Date(timestamp);
}

export function Turnover(): JSX.Element {
  const [params, setParams] = useQueryAsState();
  const variables = {
    terms: params.query?.map((e) => `${e.exclude ? '-' : ''}*${e.label}*`) ?? [],
    from: parseTimestamp(params.startDate),
    to: parseTimestamp(params.endDate),
    cursor: null,
  };
  const { data, error, fetchMore, networkStatus } = useQuery(QUERY, {
    variables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: variables.from === null || variables.to === null,
  });

  const loading = networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables;

  useEffect(() => {
    if (variables.from === null) {
      setParams({ timeframe: lastMonth() });
    }
  }, [!variables.from]);

  async function fetchNextPage(): Promise<void> {
    await fetchMore({ variables: { cursor: data?.turnovers.pageInfo.endCursor } });
  }

  const turnovers = compact(data?.turnovers.edges?.map((e) => e?.node)).map((turnover) => ({
    id: turnover.productVariant.id,
    sku: turnover.productVariant.sku,
    date:
      turnover.productVariant.product.__typename === 'Event'
        ? ([turnover.productVariant.product.happeningFrom, turnover.productVariant.product.happeningUntil] as TimeFrame)
        : null,
    product: compact([
      turnover.productVariant.product.artist,
      turnover.productVariant.product.name,
      turnover.productVariant.product.__typename === 'Event' ? turnover.productVariant.product.venue?.name : null,
      turnover.productVariant.product.__typename === 'Event'
        ? turnover.productVariant.product.venue?.address?.city
        : null,
    ]).join(', '),
    slug: turnover.productVariant.product.slug,
    variant: compact([
      turnover.productVariant.__typename === 'EventProductVariant'
        ? turnover.productVariant.pricingCategory?.name
        : null,
      turnover.productVariant.description,
      turnover.productVariant.physical ? t.model.ticket.hardticket : t.model.ticket.onlineticket,
    ]).join(' '),
    salesCount: turnover.sales?.count ?? 0,
    salesBrutPrice: turnover.sales?.brutPrice,
    salesBrutTotal: turnover.sales?.brutTotal,
    salesVatRate: turnover.sales?.vatPercentage,
    salesVat: turnover.sales?.vatTotal,
    refundsCount: turnover.refunds?.count ?? 0,
    refundsBrutPrice: turnover.refunds?.brutPrice,
    refundsBrutTotal: turnover.refunds?.brutTotal,
    refundsVatRate: turnover.refunds?.vatPercentage,
    refundsVat: turnover.refunds?.vatTotal,
  }));

  const groups = groupBy(turnovers, (turnover) => turnover.sku.split('/')[0]);
  const lines = Object.keys(groups).flatMap((sku) => {
    const lines = groups[sku];
    const example = lines[0];
    if (lines.length === 1) return [{ ...lines[0], summary: true }];
    return [
      ...lines,
      {
        ...example,
        summary: true,
        id: `${example.id}-summary`,
        sku,
        variant: t.page.turnover.summary,
        salesCount: sumBy(lines, (e) => e.salesCount),
        salesBrutPrice: null,
        salesBrutTotal: sumBy(lines, (e) => e.salesBrutTotal ?? 0),
        salesVatRate: null,
        salesVat: sumBy(lines, (e) => e.salesVat ?? 0),
        refundsCount: sumBy(lines, (e) => e.refundsCount),
        refundsBrutPrice: null,
        refundsBrutTotal: sumBy(lines, (e) => e.refundsBrutTotal ?? 0),
        refundsVatRate: null,
        refundsVat: sumBy(lines, (e) => e.refundsVat ?? 0),
      },
    ];
  });

  return (
    <Container>
      <Card
        title={t.page.turnover.title}
        elevation={2}
        fullbleed
        actions={
          <Icon
            onClick={() => runEventTurnoverExport(variables)}
            name="download"
            tooltip={t.page.turnover.downloadAsPdf}
          />
        }
        footer={
          data?.turnovers.pageInfo.hasNextPage && (
            <ViewportObserver onEnter={() => fetchNextPage()}>
              <Button
                title={t.page.turnover.loadMore}
                loading={networkStatus === NetworkStatus.fetchMore}
                onClick={fetchNextPage}
              />
            </ViewportObserver>
          )
        }
      >
        <SortableList
          condensed
          error={error}
          items={lines}
          loading={loading}
          rowClass={(row) => (row['summary'] ? 'semibold' : '')}
          actions={
            <FilterBar
              disableDefaults
              filters={['query', 'timeframe']}
              filterOrder={{
                query: 1,
                timeframe: 3,
              }}
            />
          }
          columns={[
            {
              label: t.page.turnover.date,
              dataKey: 'date',
              cellRenderer: (row) => row.date && formatRangeOfDays(row.date),
            },
            {
              label: t.page.turnover.product,
              dataKey: 'product',
              width: '70%',
              cellRenderer: (row) => <Link to={eventPath(row.slug)}>{row.product}</Link>,
            },
            {
              label: t.page.turnover.sku,
              dataKey: 'sku',
            },
            {
              label: t.page.turnover.pkVariant,
              width: '30%',
              dataKey: 'variant',
            },
            {
              label: t.page.turnover.count,
              dataKey: 'salesCount',
              labelAlign: 'right',
              cellRenderer: (row) => (
                <Stack justify="right" size="zero">
                  <div>{row.salesCount}</div>
                  {row.refundsCount > 0 && <div>{row.refundsCount}</div>}
                </Stack>
              ),
            },
            {
              label: t.page.turnover.brutPrice,
              dataKey: 'salesBrutPrice',
              labelAlign: 'right',
              cellRenderer: (row) => (
                <Stack justify="right" size="zero">
                  <PriceCell price={row.salesBrutPrice} />
                  {row.refundsCount > 0 && <PriceCell price={row.refundsBrutPrice} skipHighlight />}
                </Stack>
              ),
            },
            {
              label: t.page.turnover.brutTotal,
              dataKey: 'salesBrutTotal',
              labelAlign: 'right',
              cellRenderer: (row) => (
                <Stack justify="right" size="zero">
                  <PriceCell price={row.salesBrutTotal} />
                  {row.refundsCount > 0 && <PriceCell price={row.refundsBrutTotal} skipHighlight />}
                </Stack>
              ),
            },
            {
              label: t.page.turnover.vatRate,
              dataKey: 'salesVatRate',
              labelAlign: 'right',
              cellRenderer: (row) => (
                <Stack justify="right" size="zero">
                  <div>{formatPercentage(row.salesVatRate) || '-'}</div>
                  {row.refundsCount > 0 && <div>{formatPercentage(row.refundsVatRate) || '-'}</div>}
                </Stack>
              ),
            },
            {
              label: t.page.turnover.vat,
              dataKey: 'salesVat',
              labelAlign: 'right',
              cellRenderer: (row) => (
                <Stack justify="right" size="zero">
                  <PriceCell price={row.salesVat} />
                  {row.refundsCount > 0 && <PriceCell price={row.refundsVat} skipHighlight />}
                </Stack>
              ),
            },
          ]}
        />
      </Card>
    </Container>
  );
}
